import {
  SessionCollection,
  CreateSessionCollectionInput,
  UpdateSessionCollectionInput,
  CollectionDocument
} from '@/types/sessionCollectionTypes';
import {
  GetAccessTokenSilentlyType,
  GetAccessTokenWithPopupType
} from '@/types/transcriptionSessionTypes';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';
const COLLECTIONS_ENDPOINT = `${API_BASE_URL}/sessioncollections`;

/**
 * Helper function to acquire Auth0 access token
 */
const getAuthToken = async (
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<string> => {
  const authParams = {
    authorizationParams: {
      audience: import.meta.env.VITE_API_AUTH0_AUDIENCE
    }
  };
  try {
    return await getAccessTokenSilently(authParams);
  } catch (error) {
    console.warn(
      'Silent token acquisition failed for SessionCollectionService:',
      error
    );
    const authError = error as { error?: string };
    if (
      authError.error === 'consent_required' ||
      authError.error === 'login_required' ||
      authError.error === 'interaction_required'
    ) {
      const token = await getAccessTokenWithPopup(authParams);
      if (!token) {
        throw new Error(
          'Interactive token acquisition failed for SessionCollectionService.'
        );
      }
      return token;
    }
    throw error;
  }
};

/**
 * Get all collections for a company
 */
export const getCollectionsByCompany = async (
  companyId: string,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType,
  pageNumber: number = 1,
  pageSize: number = 50,
  searchTerm?: string
): Promise<SessionCollection[]> => {
  try {
    const token = await getAuthToken(
      getAccessTokenSilently,
      getAccessTokenWithPopup
    );

    const params = new URLSearchParams({
      pageNumber: pageNumber.toString(),
      pageSize: pageSize.toString()
    });
    if (searchTerm) {
      params.append('searchTerm', searchTerm);
    }

    const response = await fetch(
      `${COLLECTIONS_ENDPOINT}/company/${companyId}?${params.toString()}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'X-User-Id': userId
        }
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch collections: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching collections:', error);
    throw error;
  }
};

/**
 * Get a collection by ID
 */
export const getCollectionById = async (
  collectionId: string,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType,
  includeSessions: boolean = true,
  includeConversations: boolean = false,
  includeDocuments: boolean = false
): Promise<SessionCollection | null> => {
  try {
    const token = await getAuthToken(
      getAccessTokenSilently,
      getAccessTokenWithPopup
    );

    const params = new URLSearchParams({
      includeSessions: includeSessions.toString(),
      includeConversations: includeConversations.toString(),
      includeDocuments: includeDocuments.toString()
    });

    const response = await fetch(
      `${COLLECTIONS_ENDPOINT}/${collectionId}?${params.toString()}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'X-User-Id': userId
        }
      }
    );

    if (response.status === 404) {
      return null;
    }

    if (!response.ok) {
      throw new Error(`Failed to fetch collection: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching collection:', error);
    throw error;
  }
};

/**
 * Create a new collection
 */
export const createCollection = async (
  input: CreateSessionCollectionInput,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<SessionCollection> => {
  try {
    const token = await getAuthToken(
      getAccessTokenSilently,
      getAccessTokenWithPopup
    );

    const response = await fetch(`${COLLECTIONS_ENDPOINT}/create`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'X-User-Id': userId
      },
      body: JSON.stringify(input)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(
        errorData?.message ||
          `Failed to create collection: ${response.statusText}`
      );
    }

    return await response.json();
  } catch (error) {
    console.error('Error creating collection:', error);
    throw error;
  }
};

/**
 * Update a collection
 */
export const updateCollection = async (
  collectionId: string,
  input: Omit<UpdateSessionCollectionInput, 'id'>,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<SessionCollection> => {
  try {
    const token = await getAuthToken(
      getAccessTokenSilently,
      getAccessTokenWithPopup
    );

    const response = await fetch(`${COLLECTIONS_ENDPOINT}/${collectionId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'X-User-Id': userId
      },
      body: JSON.stringify({ ...input, id: collectionId })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(
        errorData?.message ||
          `Failed to update collection: ${response.statusText}`
      );
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating collection:', error);
    throw error;
  }
};

export const updateCollectionsCustomerAccess = async (
  collectionIds: string[],
  exposeToCustomerAgent: boolean,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<SessionCollection[]> => {
  return Promise.all(
    collectionIds.map((collectionId) =>
      updateCollection(
        collectionId,
        { exposeToCustomerAgent },
        userId,
        getAccessTokenSilently,
        getAccessTokenWithPopup
      )
    )
  );
};

/**
 * Delete a collection
 */
export const deleteCollection = async (
  collectionId: string,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<void> => {
  try {
    const token = await getAuthToken(
      getAccessTokenSilently,
      getAccessTokenWithPopup
    );

    const response = await fetch(`${COLLECTIONS_ENDPOINT}/${collectionId}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
        'X-User-Id': userId
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to delete collection: ${response.statusText}`);
    }
  } catch (error) {
    console.error('Error deleting collection:', error);
    throw error;
  }
};

/**
 * Add a session to a collection
 */
export const addSessionToCollection = async (
  collectionId: string,
  sessionId: string,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<void> => {
  try {
    const token = await getAuthToken(
      getAccessTokenSilently,
      getAccessTokenWithPopup
    );

    const response = await fetch(
      `${COLLECTIONS_ENDPOINT}/${collectionId}/sessions`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'X-User-Id': userId
        },
        body: JSON.stringify({ transcriptionSessionId: sessionId })
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(
        errorData?.message || `Failed to add session: ${response.statusText}`
      );
    }
  } catch (error) {
    console.error('Error adding session to collection:', error);
    throw error;
  }
};

/**
 * Remove a session from a collection
 */
export const removeSessionFromCollection = async (
  collectionId: string,
  sessionId: string,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<void> => {
  try {
    const token = await getAuthToken(
      getAccessTokenSilently,
      getAccessTokenWithPopup
    );

    const response = await fetch(
      `${COLLECTIONS_ENDPOINT}/${collectionId}/sessions/${sessionId}`,
      {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          'X-User-Id': userId
        }
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to remove session: ${response.statusText}`);
    }
  } catch (error) {
    console.error('Error removing session from collection:', error);
    throw error;
  }
};

/**
 * Get all collections that contain a specific session
 */
export const getCollectionsForSession = async (
  sessionId: string,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<SessionCollection[]> => {
  try {
    const token = await getAuthToken(
      getAccessTokenSilently,
      getAccessTokenWithPopup
    );

    const response = await fetch(
      `${COLLECTIONS_ENDPOINT}/by-session/${sessionId}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'X-User-Id': userId
        }
      }
    );

    if (!response.ok) {
      throw new Error(
        `Failed to fetch collections for session: ${response.statusText}`
      );
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching collections for session:', error);
    throw error;
  }
};

/**
 * Upload a document to a session collection
 */
export const uploadCollectionDocument = async (
  collectionId: string,
  file: File,
  documentName: string,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<CollectionDocument> => {
  try {
    const token = await getAuthToken(
      getAccessTokenSilently,
      getAccessTokenWithPopup
    );

    const formData = new FormData();
    formData.append('file', file, file.name);
    if (documentName.trim()) {
      formData.append('documentName', documentName.trim());
    }

    const response = await fetch(
      `${COLLECTIONS_ENDPOINT}/${collectionId}/documents`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'X-User-Id': userId
        },
        body: formData
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(
        errorData?.message ||
          `Failed to upload document: ${response.statusText}`
      );
    }

    const data = await response.json();
    return data.document as CollectionDocument;
  } catch (error) {
    console.error('Error uploading collection document:', error);
    throw error;
  }
};

export const processCollectionSessions = async (
  collectionId: string,
  sessionIds: string[],
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<void> => {
  const token = await getAuthToken(
    getAccessTokenSilently,
    getAccessTokenWithPopup
  );

  const response = await fetch(
    `${COLLECTIONS_ENDPOINT}/${collectionId}/process-sessions`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'X-User-Id': userId
      },
      body: JSON.stringify({ sessionIds })
    }
  );

  if (!(response.status === 202 || response.ok)) {
    const errorData = await response.json().catch(() => null);
    throw new Error(
      errorData?.message ||
        `Failed to start session processing: ${response.statusText}`
    );
  }
};

export const downloadCollectionDocument = async (
  documentId: string,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType,
  downloadFileName?: string
): Promise<boolean> => {
  try {
    const token = await getAuthToken(
      getAccessTokenSilently,
      getAccessTokenWithPopup
    );

    const response = await fetch(
      `${API_BASE_URL}/companydocuments/${documentId}/download`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'X-User-Id': userId
        }
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to download document: ${response.statusText}`);
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = downloadFileName || 'document';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    return true;
  } catch (error) {
    console.error('Error downloading collection document:', error);
    return false;
  }
};

export const createCollectionDocumentPreviewUrl = async (
  documentId: string,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<{ url: string; contentType: string }> => {
  const token = await getAuthToken(
    getAccessTokenSilently,
    getAccessTokenWithPopup
  );

  const response = await fetch(
    `${API_BASE_URL}/companydocuments/${documentId}/download`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'X-User-Id': userId
      }
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to preview document: ${response.statusText}`);
  }

  const blob = await response.blob();
  return {
    url: window.URL.createObjectURL(blob),
    contentType: response.headers.get('content-type') || blob.type
  };
};

export const reembedCompanyDocument = async (
  documentId: string,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<void> => {
  const token = await getAuthToken(
    getAccessTokenSilently,
    getAccessTokenWithPopup
  );

  const response = await fetch(
    `${API_BASE_URL}/companydocuments/${documentId}/reembed`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'X-User-Id': userId
      }
    }
  );

  if (!(response.status === 202 || response.ok)) {
    const errorData = await response.json().catch(() => null);
    throw new Error(
      errorData?.message ||
        `Failed to re-embed document: ${response.statusText}`
    );
  }
};

export const regenerateCompanyDocument = async (
  documentId: string,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<void> => {
  const token = await getAuthToken(
    getAccessTokenSilently,
    getAccessTokenWithPopup
  );

  const response = await fetch(
    `${API_BASE_URL}/companydocuments/${documentId}/regenerate`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'X-User-Id': userId
      }
    }
  );

  if (!(response.status === 202 || response.ok)) {
    const errorData = await response.json().catch(() => null);
    throw new Error(
      errorData?.message ||
        `Failed to regenerate document: ${response.statusText}`
    );
  }
};

export const regenerateCollectionDocuments = async (
  collectionId: string,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType,
  options: { failedOnly?: boolean } = {}
): Promise<void> => {
  const token = await getAuthToken(
    getAccessTokenSilently,
    getAccessTokenWithPopup
  );

  const queryParams = new URLSearchParams({
    failedOnly: (options.failedOnly === true).toString()
  });
  const queryString = queryParams.toString();

  const response = await fetch(
    `${COLLECTIONS_ENDPOINT}/${collectionId}/documents/regenerate${
      queryString ? `?${queryString}` : ''
    }`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'X-User-Id': userId
      }
    }
  );

  if (!(response.status === 202 || response.ok)) {
    const errorData = await response.json().catch(() => null);
    throw new Error(
      errorData?.message ||
        `Failed to regenerate folder documents: ${response.statusText}`
    );
  }
};
